import { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SiteShell } from "@/components/site/SiteShell";
import { PageHero } from "@/components/site/PageHero";
import { SEO, breadcrumbSchema } from "@/components/site/SEO";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Play, ChevronLeft, ChevronRight } from "lucide-react";
import { SITE } from "@/lib/siteData";
import { PORTFOLIO, PORTFOLIO_CATEGORIES, CATEGORY_LABEL } from "@/lib/portfolioData";
import { YouTubeEmbed } from "@/components/site/YouTubeEmbed";
import { ROUTE_SEO } from "@/lib/routeSeo";

const PAGE_SIZE = 24;

const getThumb = (id, isShort) =>
  isShort
    ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg`
    : `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;

// YouTube returns a 120x90 grey placeholder when maxresdefault doesn't exist.
// Detect that on load and swap to hqdefault (always available, 480x360, real frame).
const handleThumbLoad = (e, youtubeId) => {
  const img = e.currentTarget;
  if (img.naturalWidth > 0 && img.naturalWidth <= 160 && !img.dataset.fallback) {
    img.dataset.fallback = "1";
    img.src = `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`;
  }
};

const isValidCategory = (slug) =>
  slug === "all" || PORTFOLIO_CATEGORIES.some((c) => c.slug === slug);

export default function Portfolio() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-linkable filter state via ?category=slug
  const initialCategory = isValidCategory(searchParams.get("category"))
    ? searchParams.get("category")
    : "all";
  const [active, setActive] = useState(initialCategory);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);

  // Sync URL -> state (URL is source of truth for deep-linking + shareability).
  // Using functional setState so we don't need `active` in the dep array
  // (which would risk an infinite loop).
  useEffect(() => {
    const urlCat = searchParams.get("category");
    const nextCat = isValidCategory(urlCat) ? urlCat : "all";
    setActive((prev) => (prev === nextCat ? prev : nextCat));
    setPage(1);
  }, [searchParams]);

  const setCategory = (slug) => {
    setActive(slug);
    setPage(1);
    if (slug === "all") {
      searchParams.delete("category");
    } else {
      searchParams.set("category", slug);
    }
    setSearchParams(searchParams, { replace: false });
  };

  // Full filtered list
  const filtered = useMemo(() => {
    if (active === "all") return PORTFOLIO;
    return PORTFOLIO.filter(
      (p) =>
        p.category === active ||
        (Array.isArray(p.extraCategories) && p.extraCategories.includes(active)),
    );
  }, [active]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const openItem = PORTFOLIO.find((p) => p.id === openId);

  // Dynamic SEO title/description that reflects the active filter (for
  // deep-linked crawlers hitting /portfolio?category=hotel-food)
  const catLabel = active === "all" ? null : CATEGORY_LABEL[active];
  const pageTitle = catLabel
    ? `${catLabel} Portfolio | Pie in the Sky Productions`
    : ROUTE_SEO["/portfolio"].title;
  const pageDesc = catLabel
    ? `${catLabel} work by Pie in the Sky Productions - Cape Town video production. Watch commercials, films and short-form video from the ${catLabel.toLowerCase()} category.`
    : ROUTE_SEO["/portfolio"].meta;

  return (
    <SiteShell>
      <SEO
        title={pageTitle}
        description={pageDesc}
        path={active === "all" ? "/portfolio" : `/portfolio?category=${active}`}
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Portfolio", path: "/portfolio" },
          ...(catLabel ? [{ name: catLabel }] : []),
        ])}
      />
      <PageHero
        kicker="Our work"
        title={catLabel ? `${catLabel}` : ROUTE_SEO["/portfolio"].h1}
        subtitle={
          catLabel
            ? `${filtered.length} ${catLabel.toLowerCase()} project${filtered.length === 1 ? "" : "s"} - click any tile to play.`
            : `${PORTFOLIO.length} projects across ${PORTFOLIO_CATEGORIES.length} categories. Click any tile to play.`
        }
        breadcrumb={[
          { name: "Home", path: "/" },
          { name: "Portfolio", path: "/portfolio" },
          ...(catLabel ? [{ name: catLabel }] : []),
        ]}
      />
      <section className="section-pad">
        <div className="container-site">
          {/* Category chips — replaces old radix Tabs (which didn't scale well to 14 tabs) */}
          <nav
            data-testid="portfolio-category-chips"
            aria-label="Portfolio categories"
            className="mb-10 flex flex-wrap gap-2"
          >
            <button
              type="button"
              onClick={() => setCategory("all")}
              data-testid="portfolio-chip-all"
              aria-pressed={active === "all"}
              className={`rounded-full px-5 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
                active === "all"
                  ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                  : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-[hsl(var(--primary))/0.5]"
              }`}
            >
              All ({PORTFOLIO.length})
            </button>
            {PORTFOLIO_CATEGORIES.map((c) => {
              const count = PORTFOLIO.filter(
                (p) =>
                  p.category === c.slug ||
                  (Array.isArray(p.extraCategories) && p.extraCategories.includes(c.slug)),
              ).length;
              if (count === 0) return null;
              return (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => setCategory(c.slug)}
                  data-testid={`portfolio-chip-${c.slug}`}
                  aria-pressed={active === c.slug}
                  className={`rounded-full px-5 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
                    active === c.slug
                      ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                      : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-[hsl(var(--primary))/0.5]"
                  }`}
                >
                  {c.label} ({count})
                </button>
              );
            })}
          </nav>

          {/* Grid */}
          {visible.length === 0 ? (
            <p className="text-muted-foreground text-sm">No projects in this category yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {visible.map((item) => (
                <button
                  key={item.id}
                  data-testid={`portfolio-video-tile-${item.id}`}
                  onClick={() => setOpenId(item.id)}
                  className="group relative overflow-hidden rounded-xl border border-border bg-card text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
                >
                  <AspectRatio ratio={item.isShort ? 9 / 16 : 16 / 9}>
                    <img
                      src={getThumb(item.youtubeId, item.isShort)}
                      alt={item.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                      loading="lazy"
                      onLoad={(e) => handleThumbLoad(e, item.youtubeId)}
                      onError={(e) => {
                        e.currentTarget.src = `https://i.ytimg.com/vi/${item.youtubeId}/hqdefault.jpg`;
                      }}
                    />
                  </AspectRatio>
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                  {item.isShort && (
                    <div className="absolute top-4 right-4 inline-flex items-center rounded-full bg-black/60 backdrop-blur-sm border border-white/20 px-2.5 py-1 text-[9px] uppercase tracking-[0.22em] text-white">
                      Reel
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                      <Play className="h-5 w-5 fill-current" />
                    </span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 px-5 py-4">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                      {CATEGORY_LABEL[item.category] || "Pie in the Sky Productions"}
                    </div>
                    <div className="mt-1 font-display text-lg sm:text-xl uppercase text-white tracking-[0.02em] leading-tight line-clamp-2">
                      {item.title}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              data-testid="portfolio-pagination"
              className="mt-10 flex items-center justify-center gap-3"
            >
              <button
                type="button"
                data-testid="portfolio-page-prev"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-4 py-2 text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" /> Prev
              </button>
              <span
                data-testid="portfolio-page-indicator"
                className="text-xs uppercase tracking-[0.22em] text-muted-foreground px-3"
              >
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                data-testid="portfolio-page-next"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-4 py-2 text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Photography cross-link */}
          <div className="mt-14 max-w-3xl mx-auto">
            <Link
              to="/photography"
              data-testid="portfolio-photography-cross-link"
              className="group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 rounded-2xl border border-[hsl(var(--primary))/0.35] bg-[hsl(225,18%,8%)] px-7 py-6 hover:border-[hsl(var(--primary))/0.7] transition-colors"
            >
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                  Photography Portfolio
                </p>
                <p className="mt-2 font-display text-lg sm:text-xl uppercase tracking-[0.02em] text-foreground leading-tight">
                  Need stills, not motion? See our photography portfolio.
                </p>
                <p className="mt-1.5 text-xs sm:text-sm text-muted-foreground">
                  100+ photographs across hospitality, food &amp; beverage, commercial product,
                  lifestyle and editorial.
                </p>
              </div>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-foreground group-hover:text-[hsl(var(--primary))] transition-colors shrink-0">
                View photography &rarr;
              </span>
            </Link>
          </div>
          <div className="mt-12 text-center">
            <a
              data-testid="portfolio-channel-link"
              href={SITE.socials.youtube}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-block text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors border-b border-border pb-1"
            >
              View the full YouTube channel &rarr;
            </a>
          </div>
        </div>
      </section>
      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent
          data-testid="portfolio-video-dialog"
          className="max-w-5xl w-[94vw] p-0 bg-black border-border overflow-hidden"
        >
          <DialogTitle className="sr-only">{openItem?.title || ""}</DialogTitle>
          {openItem && <YouTubeEmbed videoId={openItem.youtubeId} title={openItem.title} />}
          {openItem && (
            <div className="px-5 py-4 border-t border-border bg-card">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                {CATEGORY_LABEL[openItem.category]}
              </div>
              <div className="mt-1 font-display text-xl uppercase text-foreground">
                {openItem.title}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </SiteShell>
  );
}
